import { memo, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { formatLocalAddress } from 'Utils';
import { Colors, ModalProps } from 'Types';
import { Button, ModalBox, ModalBoxFooter } from 'Elements';

interface Props extends ModalProps {
	suggestedAddress: any;
	enteredAddress: any;
	isLoading: boolean;
	onSubmit: (data: any) => void;
}

export const ModalBoxSuggestedAddress: FC<Props> = memo(
	({
		isOpen,
		onClose,
		enteredAddress,
		isLoading,
		suggestedAddress,
		onSubmit
	}) => {
		const { t } = useTranslation();

		return (
			<ModalBox
				isOpen={isOpen}
				size="lg"
				onClose={onClose}
				title={t('title.suggested-address')}>
				<div className="flex flex-col gap-2">
					<span className="fs-base lh-base text-gray-3">
						{t(
							'text.the-specified-address-does-not-appear-to-exist'
						)}
						:
					</span>
					<div className="fs-base lh-base fw-600">
						{formatLocalAddress(
							enteredAddress.street,
							enteredAddress.houseNumber,
							enteredAddress.postalCode,
							enteredAddress.city,
							enteredAddress.state?.label,
							enteredAddress.country?.label
						)}
					</div>
					<p className="fs-base lh-base text-gray-3 mt-4">
						{t(
							'text.you-can-use-the-suggested-address-or-the-one-you-entered'
						)}
					</p>
					<span className="fs-base lh-base text-gray-3 mt-4">
						{t('text.current-address')}:
					</span>
					<div className="fs-base lh-base fw-600">
						{formatLocalAddress(
							suggestedAddress?.address?.streetName,
							suggestedAddress?.address?.streetNumber,
							suggestedAddress?.address?.postalCode,
							suggestedAddress?.address?.municipality,
							suggestedAddress?.address?.countrySubdivisionName,
							suggestedAddress?.address?.country
						)}
					</div>
				</div>
				<ModalBoxFooter className="justify-content-between mt-4">
					<Button
						type="button"
						disabled={isLoading}
						color={
							isLoading
								? Colors['gray-2']
								: Colors['border-primary']
						}
						onClick={() => {
							const data = {
								street: suggestedAddress?.address?.streetName,
								houseNumber:
									suggestedAddress?.address?.streetNumber,
								postalCode:
									suggestedAddress?.address?.postalCode,
								city: suggestedAddress?.address?.municipality,
								state: {
									label: suggestedAddress?.address
										?.countrySubdivisionName,
									value: suggestedAddress?.address
										?.countrySubdivisionName
								},

								location: {
									longitude: suggestedAddress?.position?.lon,
									latitude: suggestedAddress?.position?.lat
								},
								countryIso2:
									suggestedAddress.address.countryCode,
								countryIso3:
									suggestedAddress.address.countryCodeISO3,
								countryName: enteredAddress?.country?.label
							};
							onSubmit(data);
						}}>
						{isLoading
							? t('forms.validating-address')
							: t('button.suggested-address')}
					</Button>
					<Button
						type="button"
						disabled={isLoading}
						color={isLoading ? Colors['gray-2'] : Colors.primary}
						onClick={() => {
							const data = {
								...enteredAddress,
								location: {
									longitude: suggestedAddress?.position?.lon,
									latitude: suggestedAddress?.position?.lat
								}
							};
							onSubmit(data);
						}}>
						{isLoading
							? t('forms.validating-address')
							: t('button.use-entered-address')}
					</Button>
				</ModalBoxFooter>
			</ModalBox>
		);
	}
);

ModalBoxSuggestedAddress.displayName = 'ModalBoxSuggestedAddress';
